import { useMemo, useState } from 'react';
import { MessageCircle, CheckCircle2 } from 'lucide-react';
import { PageHeader } from '../ui/PageHeader';
import { Button } from '../ui/Button';
import { Card, CardBody } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { DataTable } from '../ui/DataTable';
import { EmptyState } from '../ui/EmptyState';
import { SegmentedControl } from '../ui/SegmentedControl';
import { SkeletonCard } from '../ui/Skeleton';
import { buildRenewalWhatsAppUrl } from '../../utils/renewalWhatsApp';
import { openExternalUrl } from '../../utils/openExternalUrl';
import {
  daysUntilExpiry,
  formatSubscriptionDate,
  isSubscriptionExpired,
} from '../../utils/academicYear';
import { PRICE_PER_CLASS_YEAR } from '../../constants/invoiceIssuer';

function renewalBucket(head) {
  if (isSubscriptionExpired(head.subscription_expires_at)) return 'expired';
  const days = daysUntilExpiry(head.subscription_expires_at);
  if (days !== null && days <= 14) return 'soon14';
  if (days !== null && days <= 30) return 'soon30';
  return 'other';
}

export function AdminRenewalsPanel({
  labels,
  heads,
  isLoading,
  onRefresh,
  onMarkPaid,
  markingPaidId,
}) {
  const [bucket, setBucket] = useState('all');

  const renewalHeads = useMemo(() => {
    return (heads || [])
      .filter((h) => {
        const b = renewalBucket(h);
        if (bucket === 'all') return b !== 'other';
        return b === bucket;
      })
      .sort((a, b) => {
        const da = daysUntilExpiry(a.subscription_expires_at);
        const db = daysUntilExpiry(b.subscription_expires_at);
        if (da === null && db === null) return 0;
        if (da === null) return 1;
        if (db === null) return -1;
        return da - db;
      });
  }, [heads, bucket]);

  const columns = [
    {
      key: 'name',
      label: labels.name,
      render: (h) => (
        <div>
          <p className="font-medium text-text">{h.name}</p>
          <p className="text-xs text-muted">@{h.username}</p>
        </div>
      ),
    },
    {
      key: 'divisions',
      label: labels.numDivisionsLabel,
      render: (h) => (
        <span className="tabular-nums font-medium">{h.divisions ?? 0}</span>
      ),
    },
    {
      key: 'fee',
      label: labels.estimatedYearlyFee,
      render: (h) => {
        const n = parseInt(h.divisions, 10) || 0;
        return <span className="tabular-nums">₹{n * PRICE_PER_CLASS_YEAR}</span>;
      },
    },
    {
      key: 'exp',
      label: labels.subscriptionValidUntil,
      render: (h) => {
        const exp = h.subscription_expires_at;
        if (!exp) return <Badge variant="default">—</Badge>;
        if (isSubscriptionExpired(exp)) {
          return <Badge variant="warning">{labels.subscriptionExpired}</Badge>;
        }
        const days = daysUntilExpiry(exp);
        if (days !== null && days <= 14) {
          return (
            <Badge variant="warning">
              {formatSubscriptionDate(exp)} ({days}d)
            </Badge>
          );
        }
        return <Badge variant="success">{formatSubscriptionDate(exp)}</Badge>;
      },
    },
    {
      key: 'actions',
      label: labels.actions,
      render: (h) => (
        <div className="flex flex-wrap justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            aria-label={labels.renewalsWhatsApp}
            onClick={() => openExternalUrl(buildRenewalWhatsAppUrl(h))}
          >
            <MessageCircle className="h-4 w-4" />
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onMarkPaid(h)}
            isLoading={markingPaidId === h.id}
          >
            <CheckCircle2 className="h-4 w-4" />
            {labels.renewalsMarkPaid}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title={labels.renewalsTitle}
        actions={
          <Button variant="secondary" size="sm" onClick={onRefresh} isLoading={isLoading}>
            {labels.refresh}
          </Button>
        }
      />

      <p className="text-sm text-muted">{labels.renewalsSubtitle}</p>

      <SegmentedControl
        options={[
          { value: 'all', label: labels.renewalsFilterAll },
          { value: 'expired', label: labels.filterValidPresetExpired },
          { value: 'soon14', label: labels.renewalsFilter14 },
          { value: 'soon30', label: labels.filterValidPreset30 },
        ]}
        value={bucket}
        onChange={setBucket}
      />

      {isLoading ? (
        <SkeletonCard />
      ) : renewalHeads.length === 0 ? (
        <EmptyState title={labels.renewalsEmpty} message={labels.renewalsEmptyHint} />
      ) : (
        <DataTable
          columns={columns}
          data={renewalHeads}
          emptyMessage={labels.noData}
          renderMobileCard={(h) => {
            const n = parseInt(h.divisions, 10) || 0;
            return (
              <Card>
                <CardBody className="space-y-3">
                  <div>
                    <p className="font-semibold text-text">{h.name}</p>
                    <p className="text-sm text-muted">@{h.username}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 text-sm">
                    <span>
                      {labels.numDivisionsLabel}: {h.divisions ?? 0}
                    </span>
                    <span>·</span>
                    <span>
                      {labels.estimatedYearlyFee}: ₹{n * PRICE_PER_CLASS_YEAR}
                    </span>
                  </div>
                  <div>{columns[3].render(h)}</div>
                  <div className="flex flex-wrap gap-2 border-t border-border pt-3">
                    {columns[4].render(h)}
                  </div>
                </CardBody>
              </Card>
            );
          }}
        />
      )}
    </div>
  );
}
